import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  User,
  Shield,
  Bell,
  Palette,
  CreditCard,
  Key,
  Globe,
  Save,
  Settings as SettingsIcon,
  TrendingUp,
  AlertTriangle,
  Zap,
  Target,
  BarChart3,
  Clock,
  DollarSign,
  Home,
  Briefcase,
} from 'lucide-react';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    displayName: '',
    email: '',
    phone: '',
    country: '',
    timezone: '',
    currency: 'USD'
  });
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    sms: false,
    priceAlerts: true,
    tradeConfirmations: true,
    weeklyReports: false,
    riskWarnings: true,
    marketUpdates: true,
    economicCalendar: false,
    newsUpdates: true,
    socialTrading: false,
    affiliateProgram: true
  });
  const [tradingSettings, setTradingSettings] = useState({
    defaultLeverage: '100:1',
    maxPositionSize: '10000',
    defaultStopLoss: '2%',
    defaultTakeProfit: '4%',
    newsTrading: true,
    weekendTrading: false,
    autoClosePositions: true,
    riskRewardRatio: '1:2'
  });
  const [riskSettings, setRiskSettings] = useState({
    maxDailyLoss: '5%',
    maxWeeklyLoss: '15%',
    maxMonthlyLoss: '25%',
    maxDrawdown: '20%',
    positionSizing: '2%',
    correlationLimit: '0.7',
    volatilityFilter: true,
    marketHoursOnly: true
  });

  // Initialize profile data from user context
  useEffect(() => {
    if (user) {
      // Load profile data from localStorage if available
      const savedProfile = localStorage.getItem('userProfile');
      const savedData = savedProfile ? JSON.parse(savedProfile) : {};
      
      setProfileData({
        displayName: user.displayName || '',
        email: user.email || '',
        phone: savedData.phone || '',
        country: savedData.country || user.metadata?.signup?.country || '',
        timezone: savedData.timezone || user.metadata?.signup?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        currency: savedData.currency || 'USD'
      });

      // Auto-detect country if not already set
      if (!savedData.country && !user.metadata?.signup?.country) {
        collectUserMetadata().then(metadata => {
          if (metadata.country) {
            setProfileData(prev => ({ ...prev, country: metadata.country }));
          }
        });
      }
    }
  }, [user]);

  // Function to collect user metadata
  const collectUserMetadata = async () => {
    try {
      // Try to get IP and location data
      const response = await fetch('https://ipapi.co/json/');
      if (response.ok) {
        const data = await response.json();
        return {
          country: data.country_name || '',
          city: data.city || '',
          ipAddress: data.ip || ''
        };
      }
    } catch (error) {
      console.warn('Failed to get location data:', error);
    }
    return { country: '', city: '', ipAddress: '' };
  };

  const handleProfileUpdate = async () => {
    if (user) {
      try {
        // Update user profile in Firebase
        await updateUser({
          displayName: profileData.displayName
        });
        
        // Collect current user metadata for country/timezone
        const metadata = await collectUserMetadata();
        const currentCountry = profileData.country || metadata.country;
        
        // Save additional profile data to localStorage
        localStorage.setItem('userProfile', JSON.stringify({
          phone: profileData.phone,
          country: currentCountry,
          timezone: profileData.timezone,
          currency: profileData.currency,
          metadata: {
            ...metadata,
            timestamp: new Date()
          }
        }));
        
        // Update local state with detected country if not already set
        if (!profileData.country && metadata.country) {
          setProfileData(prev => ({ ...prev, country: metadata.country }));
        }
        
        alert('Profile updated successfully!');
      } catch (error) {
        console.error('Error updating profile:', error);
        alert('Failed to update profile. Please try again.');
      }
    }
  };

  const handleTradingSettingsUpdate = async () => {
    try {
      // Save trading settings to localStorage or Firestore
      localStorage.setItem('tradingSettings', JSON.stringify(tradingSettings));
      alert('Trading settings updated successfully!');
    } catch (error) {
      console.error('Error updating trading settings:', error);
      alert('Failed to update trading settings. Please try again.');
    }
  };

  const handleRiskSettingsUpdate = async () => {
    try {
      // Save risk settings to localStorage or Firestore
      localStorage.setItem('riskSettings', JSON.stringify(riskSettings));
      alert('Risk settings updated successfully!');
    } catch (error) {
      console.error('Error updating risk settings:', error);
      alert('Failed to update risk settings. Please try again.');
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'trading', name: 'Trading', icon: TrendingUp },
    { id: 'risk', name: 'Risk Management', icon: AlertTriangle },
    { id: 'billing', name: 'Billing', icon: CreditCard },
  ];

  const navigationMenu = [
    { name: 'Dashboard', href: '/miai', icon: Home, current: false },
    { name: 'Portfolio', href: '/portfolio', icon: Briefcase, current: false },
    { name: 'Analytics', href: '/analytics', icon: BarChart3, current: false },
    { name: 'Settings', href: '/settings', icon: SettingsIcon, current: true },
  ];

  const handleSave = () => {
    // Save settings logic here
  };

  return (
    <div className="h-screen overflow-hidden flex flex-col">
      {/* Navigation Menu */}
      <div className="card p-4 flex-shrink-0">
        <nav className="flex space-x-8">
          {navigationMenu.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.name}
                onClick={() => navigate(item.href)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  item.current
                    ? 'bg-[#2AB7A6] text-black shadow-lg'
                    : 'theme-text-quaternary hover:theme-text-secondary hover:theme-bg-quaternary'
                }`}
              >
                <Icon className={`h-4 w-4 ${item.current ? 'text-black' : 'theme-text-quaternary'}`} />
                <span>{item.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="space-y-6 p-6">
          {/* Enhanced Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold theme-text-primary flex items-center">
                <SettingsIcon className="h-6 w-6 mr-3 text-[#2AB7A6]" />
                Settings
              </h1>
              <p className="theme-text-quaternary mt-2">
                Manage your account preferences, security settings, and trading configurations
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button onClick={handleSave} className="btn-primary flex items-center justify-center">
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </button>
            </div>
          </div>

          {/* Enhanced Settings Tabs */}
          <div className="card p-4">
            <div className="border-b theme-border">
              <nav className="-mb-px flex space-x-8">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                        activeTab === tab.id
                          ? 'border-[#2AB7A6] text-[#2AB7A6]'
                          : 'border-transparent theme-text-quaternary hover:theme-text-secondary hover:theme-border'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{tab.name}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Enhanced Tab Content */}
            <div className="mt-6">
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold theme-text-primary flex items-center">
                    <User className="h-5 w-5 mr-2 text-[#2AB7A6]" />
                    Profile Information
                  </h3>
                  
                  {/* Account Information (Read-only) */}
                  <div className={`${theme === 'light' ? 'bg-gray-100' : 'bg-gray-800'} rounded-lg p-4 border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
                    <h4 className="text-md font-medium theme-text-secondary mb-3">Account Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium theme-text-quaternary mb-1">Account Status</label>
                        <div className="text-sm theme-text-primary">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            user?.status === 'approved' ? 'bg-[#2AB7A6]/20 text-[#2AB7A6]' :
                            user?.status === 'pending' ? 'bg-yellow-900 text-yellow-300' :
                            user?.status === 'rejected' ? 'bg-red-900 text-red-300' :
                            user?.status === 'suspended' ? 'bg-red-900 text-red-300' :
                            theme === 'light' ? 'bg-gray-200 text-gray-700' : 'bg-gray-700 text-gray-300'
                          }`}>
                            {user?.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : 'Unknown'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium theme-text-quaternary mb-1">Account Role</label>
                        <div className="text-sm theme-text-primary">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-900 text-blue-300">
                            {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium theme-text-quaternary mb-1">Member Since</label>
                        <div className="text-sm theme-text-primary">
                          {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }) : 'Unknown'}
                        </div>
                      </div>
                      {user?.lastLoginAt && (
                        <div>
                          <label className="block text-xs font-medium theme-text-quaternary mb-1">Last Login</label>
                          <div className="text-sm theme-text-primary">
                            {new Date(user.lastLoginAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      )}
                      {user?.uid && (
                        <div>
                          <label className="block text-xs font-medium theme-text-quaternary mb-1">User ID</label>
                          <div className="flex items-center space-x-2">
                            <div className="text-sm theme-text-primary font-mono text-xs break-all flex-1">
                              {user.uid}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText(user.uid);
                                alert('User ID copied to clipboard!');
                              }}
                              className={`px-2 py-1 ${theme === 'light' ? 'bg-gray-300 text-gray-700 hover:bg-gray-400' : 'bg-gray-600 text-gray-300 hover:bg-gray-500'} text-xs font-medium rounded transition-colors duration-200 flex items-center justify-center`}
                              title="Copy User ID to clipboard"
                            >
                              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Editable Profile Fields */}
                  <div className={`${theme === 'light' ? 'bg-gray-100' : 'bg-gray-800'} rounded-lg p-4 border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
                    <h4 className="text-md font-medium theme-text-secondary mb-3">Personal Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium theme-text-secondary mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={profileData.displayName}
                          onChange={(e) => setProfileData({ ...profileData, displayName: e.target.value })}
                          className="input-field"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium theme-text-secondary mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          value={profileData.email}
                          disabled
                          className={`input-field ${theme === 'light' ? 'bg-gray-200' : 'bg-gray-700'} cursor-not-allowed`}
                        />
                        <p className="text-xs theme-text-quaternary mt-1">Email cannot be changed</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium theme-text-secondary mb-2">
                          Phone
                        </label>
                        <div className="flex space-x-2">
                          <input
                            type="tel"
                            value={profileData.phone}
                            onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                            className="input-field flex-1"
                          />
                          {profileData.phone && (
                            <button
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText(profileData.phone);
                                alert('Phone number copied to clipboard!');
                              }}
                              className={`px-2 py-2 ${theme === 'light' ? 'bg-gray-300 text-gray-700 hover:bg-gray-400' : 'bg-gray-600 text-gray-300 hover:bg-gray-500'} text-xs font-medium rounded transition-colors duration-200 flex items-center justify-center`}
                              title="Copy phone number to clipboard"
                            >
                              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium theme-text-secondary mb-2">
                          Country
                        </label>
                        <div className="flex space-x-2">
                          <select
                            value={profileData.country}
                            onChange={(e) => setProfileData({ ...profileData, country: e.target.value })}
                            className="input-field flex-1"
                          >
                            <option value="">Select Country</option>
                            <option value="United States">United States</option>
                            <option value="United Kingdom">United Kingdom</option>
                            <option value="Canada">Canada</option>
                            <option value="Australia">Australia</option>
                            <option value="Germany">Germany</option>
                            <option value="France">France</option>
                            <option value="Japan">Japan</option>
                            <option value="Singapore">Singapore</option>
                          </select>
                          <button
                            type="button"
                            onClick={async () => {
                              const metadata = await collectUserMetadata();
                              if (metadata.country) {
                                setProfileData(prev => ({ ...prev, country: metadata.country }));
                              }
                            }}
                            className="px-3 py-2 bg-[#2AB7A6] text-black text-xs font-medium rounded-lg hover:bg-[#25A395] transition-colors duration-200 flex items-center justify-center"
                            title="Detect your location automatically"
                          >
                            <Globe className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium theme-text-secondary mb-2">
                          Time Zone
                        </label>
                        <select
                          value={profileData.timezone}
                          onChange={(e) => setProfileData({ ...profileData, timezone: e.target.value })}
                          className="input-field"
                        >
                          <option value="UTC-8 (Pacific Time)">UTC-8 (Pacific Time)</option>
                          <option value="UTC-5 (Eastern Time)">UTC-5 (Eastern Time)</option>
                          <option value="UTC+0 (GMT)">UTC+0 (GMT)</option>
                          <option value="UTC+1 (Central European)">UTC+1 (Central European)</option>
                          <option value="UTC+5:30 (India)">UTC+5:30 (India)</option>
                          <option value="UTC+8 (China)">UTC+8 (China)</option>
                          <option value="UTC+9 (Japan)">UTC+9 (Japan)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium theme-text-secondary mb-2">
                          Preferred Currency
                        </label>
                        <select
                          value={profileData.currency}
                          onChange={(e) => setProfileData({ ...profileData, currency: e.target.value })}
                          className="input-field"
                        >
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                          <option value="JPY">JPY</option>
                          <option value="AUD">AUD</option>
                          <option value="CAD">CAD</option>
                          <option value="CHF">CHF</option>
                        </select>
                      </div>
                    </div>
                    <div className="mt-6">
                      <button onClick={handleProfileUpdate} className="btn-primary flex items-center justify-center">
                        <Save className="h-4 w-4 mr-2" />
                        Save Profile
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold theme-text-primary flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-[#2AB7A6]" />
                    Security Settings
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Current Password
                      </label>
                      <input
                        type="password"
                        placeholder="Enter current password"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        New Password
                      </label>
                      <input
                        type="password"
                        placeholder="Enter new password"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        placeholder="Confirm new password"
                        className="input-field"
                      />
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="2fa"
                        className="rounded border-gray-600 text-[#2AB7A6] focus:ring-[#2AB7A6] focus:ring-offset-gray-900"
                      />
                      <label htmlFor="2fa" className="text-sm theme-text-secondary">
                        Enable Two-Factor Authentication
                      </label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="sessionTimeout"
                        className="rounded border-gray-600 text-[#2AB7A6] focus:ring-[#2AB7A6] focus:ring-offset-gray-900"
                      />
                      <label htmlFor="sessionTimeout" className="text-sm theme-text-secondary">
                        Auto-logout after 30 minutes of inactivity
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'notifications' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold theme-text-primary flex items-center">
                    <Bell className="h-5 w-5 mr-2 text-[#2AB7A6]" />
                    Notification Preferences
                  </h3>
                  <div className="space-y-4">
                    {Object.entries(notifications).map(([key, value]) => (
                      <div key={key} className={`flex items-center justify-between p-3 ${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} rounded-lg border ${theme === 'light' ? 'border-gray-200' : 'border-gray-600'}`}>
                        <div>
                          <div className="text-sm font-medium theme-text-primary">
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </div>
                          <div className="text-xs theme-text-quaternary">
                            Receive {key.replace(/([A-Z])/g, ' $1').toLowerCase()} notifications
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={e =>
                            setNotifications({
                              ...notifications,
                              [key]: e.target.checked,
                            })
                          }
                          className="rounded border-gray-600 text-[#2AB7A6] focus:ring-[#2AB7A6] focus:ring-offset-gray-900"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'trading' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold theme-text-primary flex items-center">
                    <TrendingUp className="h-5 w-5 mr-2 text-[#2AB7A6]" />
                    Trading Preferences
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Default Leverage
                      </label>
                      <select
                        value={tradingSettings.defaultLeverage}
                        onChange={e =>
                          setTradingSettings({
                            ...tradingSettings,
                            defaultLeverage: e.target.value,
                          })
                        }
                        className="input-field"
                      >
                        <option value="10:1">10x</option>
                        <option value="25:1">25x</option>
                        <option value="50:1">50x</option>
                        <option value="100:1">100x</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Max Position Size
                      </label>
                      <input
                        type="number"
                        value={tradingSettings.maxPositionSize}
                        onChange={e =>
                          setTradingSettings({
                            ...tradingSettings,
                            maxPositionSize: e.target.value,
                          })
                        }
                        className="input-field"
                        min="1000"
                        max="100000"
                        step="1000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Default Stop Loss (%)
                      </label>
                      <input
                        type="number"
                        value={tradingSettings.defaultStopLoss.replace('%', '')}
                        onChange={e =>
                          setTradingSettings({
                            ...tradingSettings,
                            defaultStopLoss: e.target.value + '%',
                          })
                        }
                        className="input-field"
                        min="0.5"
                        max="10"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Default Take Profit (%)
                      </label>
                      <input
                        type="number"
                        value={tradingSettings.defaultTakeProfit.replace('%', '')}
                        onChange={e =>
                          setTradingSettings({
                            ...tradingSettings,
                            defaultTakeProfit: e.target.value + '%',
                          })
                        }
                        className="input-field"
                        min="1"
                        max="20"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Risk/Reward Ratio
                      </label>
                      <select
                        value={tradingSettings.riskRewardRatio}
                        onChange={e =>
                          setTradingSettings({
                            ...tradingSettings,
                            riskRewardRatio: e.target.value,
                          })
                        }
                        className="input-field"
                      >
                        <option value="1:1">1:1</option>
                        <option value="1:2">1:2</option>
                        <option value="1:3">1:3</option>
                        <option value="1:4">1:4</option>
                      </select>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="newsTrading"
                        checked={tradingSettings.newsTrading}
                        onChange={e =>
                          setTradingSettings({
                            ...tradingSettings,
                            newsTrading: e.target.checked,
                          })
                        }
                        className="rounded border-gray-600 text-[#2AB7A6] focus:ring-[#2AB7A6] focus:ring-offset-gray-900"
                      />
                      <label htmlFor="newsTrading" className="text-sm theme-text-secondary">
                        Allow News Trading
                      </label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="weekendTrading"
                        checked={tradingSettings.weekendTrading}
                        onChange={e =>
                          setTradingSettings({
                            ...tradingSettings,
                            weekendTrading: e.target.checked,
                          })
                        }
                        className="rounded border-gray-600 text-[#2AB7A6] focus:ring-[#2AB7A6] focus:ring-offset-gray-900"
                      />
                      <label htmlFor="weekendTrading" className="text-sm theme-text-secondary">
                        Allow Weekend Trading
                      </label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="autoClosePositions"
                        checked={tradingSettings.autoClosePositions}
                        onChange={e =>
                          setTradingSettings({
                            ...tradingSettings,
                            autoClosePositions: e.target.checked,
                          })
                        }
                        className="rounded border-gray-600 text-[#2AB7A6] focus:ring-[#2AB7A6] focus:ring-offset-gray-900"
                      />
                      <label htmlFor="autoClosePositions" className="text-sm theme-text-secondary">
                        Auto-close Positions at End of Day
                      </label>
                    </div>
                    <div className="mt-6">
                      <button onClick={handleTradingSettingsUpdate} className="btn-primary flex items-center justify-center">
                        <Save className="h-4 w-4 mr-2" />
                        Save Trading Settings
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'risk' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold theme-text-primary flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2 text-[#2AB7A6]" />
                    Risk Management
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Max Daily Loss (%)
                      </label>
                      <input
                        type="number"
                        value={riskSettings.maxDailyLoss.replace('%', '')}
                        onChange={e =>
                          setRiskSettings({
                            ...riskSettings,
                            maxDailyLoss: e.target.value + '%',
                          })
                        }
                        className="input-field"
                        min="1"
                        max="20"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Max Weekly Loss (%)
                      </label>
                      <input
                        type="number"
                        value={riskSettings.maxWeeklyLoss.replace('%', '')}
                        onChange={e =>
                          setRiskSettings({
                            ...riskSettings,
                            maxWeeklyLoss: e.target.value + '%',
                          })
                        }
                        className="input-field"
                        min="5"
                        max="50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Max Monthly Loss (%)
                      </label>
                      <input
                        type="number"
                        value={riskSettings.maxMonthlyLoss.replace('%', '')}
                        onChange={e =>
                          setRiskSettings({
                            ...riskSettings,
                            maxMonthlyLoss: e.target.value + '%',
                          })
                        }
                        className="input-field"
                        min="10"
                        max="100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Max Drawdown (%)
                      </label>
                      <input
                        type="number"
                        value={riskSettings.maxDrawdown.replace('%', '')}
                        onChange={e =>
                          setRiskSettings({
                            ...riskSettings,
                            maxDrawdown: e.target.value + '%',
                          })
                        }
                        className="input-field"
                        min="5"
                        max="50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Position Sizing (%)
                      </label>
                      <input
                        type="number"
                        value={riskSettings.positionSizing.replace('%', '')}
                        onChange={e =>
                          setRiskSettings({
                            ...riskSettings,
                            positionSizing: e.target.value + '%',
                          })
                        }
                        className="input-field"
                        min="0.5"
                        max="10"
                        step="0.5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-2">
                        Correlation Limit
                      </label>
                      <input
                        type="number"
                        value={riskSettings.correlationLimit}
                        onChange={e =>
                          setRiskSettings({
                            ...riskSettings,
                            correlationLimit: e.target.value,
                          })
                        }
                        className="input-field"
                        min="0.1"
                        max="1.0"
                        step="0.1"
                      />
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="volatilityFilter"
                        checked={riskSettings.volatilityFilter}
                        onChange={e =>
                          setRiskSettings({
                            ...riskSettings,
                            volatilityFilter: e.target.checked,
                          })
                        }
                        className="rounded border-gray-600 text-[#2AB7A6] focus:ring-[#2AB7A6] focus:ring-offset-gray-900"
                      />
                      <label htmlFor="volatilityFilter" className="text-sm theme-text-secondary">
                        Enable Volatility Filter
                      </label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="marketHoursOnly"
                        checked={riskSettings.marketHoursOnly}
                        onChange={e =>
                          setRiskSettings({
                            ...riskSettings,
                            marketHoursOnly: e.target.checked,
                          })
                        }
                        className="rounded border-gray-600 text-[#2AB7A6] focus:ring-[#2AB7A6] focus:ring-offset-gray-900"
                      />
                      <label htmlFor="marketHoursOnly" className="text-sm theme-text-secondary">
                        Trade Only During Market Hours
                      </label>
                    </div>
                    <div className="mt-6">
                      <button onClick={handleRiskSettingsUpdate} className="btn-primary flex items-center justify-center">
                        <Save className="h-4 w-4 mr-2" />
                        Save Risk Settings
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'billing' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold theme-text-primary flex items-center">
                    <CreditCard className="h-5 w-5 mr-2 text-[#2AB7A6]" />
                    Billing Information
                  </h3>
                  <div className={`${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} rounded-lg p-4 border ${theme === 'light' ? 'border-gray-200' : 'border-gray-600'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-sm font-medium theme-text-primary">
                          Current Plan
                        </div>
                        <div className="text-lg font-semibold theme-text-primary">
                          Pro Trader
                        </div>
                      </div>
                      <span className="text-sm theme-text-quaternary">$99/month</span>
                    </div>
                    <div className="space-y-2 text-sm theme-text-quaternary">
                      <div>• Unlimited trades</div>
                      <div>• Advanced analytics</div>
                      <div>• Priority support</div>
                      <div>• API access</div>
                      <div>• Risk management tools</div>
                    </div>
                  </div>
                  <button className="btn-secondary flex items-center justify-center">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Manage Subscription
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Add padding at the end */}
        <div className="pb-[50px]"></div>
      </div>
    </div>
  );
};

export default Settings;
